// Machines: one row per physical machine; its studio enrolments in the right panel.
import React from 'react';
import type { PhysicalMachine } from '../../../shared/types';
import { GAsyncState, GCard, GEyebrow, GH2, GPill, inputStyle } from '../design/atoms';
import { DbPending, isDbUnavailable } from '../design/DbPending';
import { DetailHeader, DetailSection, MasterDetail, ROW_CAP, RowCapNote, selectedRow, useEscape } from '../design/MasterDetail';
import { useResource } from '../store/resourceStore';
import { useSelection } from '../store/uiStore';

function Detail({ m, onClose }: { m: PhysicalMachine; onClose: () => void }) {
  return (
    <>
      <DetailHeader title={m.name} eyebrow={`${m.enrolments.length} studio enrolment${m.enrolments.length === 1 ? '' : 's'}${m.labels.length > 1 ? ` · also named ${m.labels.filter((l) => l !== m.name).join(', ')}` : ''}`} onClose={onClose} />
      <DetailSection label="Enrolments">
        <table className="pd" style={{ fontSize: 12 }}>
          <thead><tr><th>Studio</th><th>Name there</th><th className="num">Roles</th></tr></thead>
          <tbody>{m.enrolments.map((e) => (
            <tr key={e.studioMachineId} title={`studio_machine ${e.studioMachineId} · studio ${e.studioId}`}><td>{e.studioLabel}</td><td>{e.label}</td><td className="num">{e.roles}</td></tr>
          ))}</tbody>
        </table>
      </DetailSection>
      <DetailSection label="Ids">
        <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-4)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {m.enrolments.map((e) => <div key={e.studioMachineId}>{e.studioMachineId} <span style={{ color: 'var(--fg-5)' }}>in</span> {e.studioId}</div>)}
        </div>
      </DetailSection>
    </>
  );
}

export function Machines() {
  const r = useResource<PhysicalMachine[]>('/api/machines');
  const [q, setQ] = React.useState('');
  const [open, setOpen] = useSelection('machines');
  const close = React.useCallback(() => setOpen(null), [setOpen]);
  useEscape(close, !!open);
  const filtered = (r.data ?? []).filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()) || m.enrolments.some((e) => e.studioLabel.toLowerCase().includes(q.toLowerCase()) || e.studioMachineId.includes(q)));
  const rows = filtered.slice(0, ROW_CAP);
  const enrolments = (r.data ?? []).reduce((s, m) => s + m.enrolments.length, 0);
  const current = (r.data ?? []).find((m) => m.key === open) ?? null;
  const list = isDbUnavailable(r.error) ? <DbPending error={r.error!} onRetry={r.reload} /> : (
    <GCard padding="18px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <GH2 size={17}>Machines</GH2>
        <GEyebrow style={{ fontSize: 12 }}>{r.data ? `${r.data.length} physical machine${r.data.length === 1 ? '' : 's'} · ${enrolments} studio enrolment${enrolments === 1 ? '' : 's'} · grouped by a one-way digest of the fingerprint` : ''}</GEyebrow>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search machine or studio" style={{ ...inputStyle, width: 260, padding: '7px 12px' }} />
        <GPill onClick={r.reload} disabled={r.loading}>Refresh</GPill>
      </div>
      <GAsyncState busy={r.loading && !r.data} error={r.error} onRetry={r.reload} />
      {r.data && (r.data.length === 0 ? <div style={{ fontFamily: 'var(--ff-code)', fontSize: 12.5, color: 'var(--fg-4)' }}>No machines enrolled.</div> : (
        <>
          <table className="pd">
            <thead><tr><th>Machine</th><th>Studios</th><th className="num">Enrolments</th><th className="num">Roles</th></tr></thead>
            <tbody>{rows.map((m) => (
              <tr key={m.key} className="row-hover" aria-selected={open === m.key || undefined} style={selectedRow(open === m.key)} onClick={() => setOpen(m.key)}>
                <td>{m.name}{m.labels.length > 1 && <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-4)' }}>also named {m.labels.filter((l) => l !== m.name).join(', ')}</div>}</td>
                <td><div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{m.enrolments.map((e) => <span key={e.studioMachineId} style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, padding: '2px 8px', borderRadius: 'var(--r-pill)', border: '1px solid var(--bd)', color: 'var(--fg-2)' }}>{e.studioLabel}</span>)}</div></td>
                <td className="num">{m.enrolments.length}</td>
                <td className="num">{m.enrolments.reduce((s, e) => s + e.roles, 0)}</td>
              </tr>
            ))}</tbody>
          </table>
          <RowCapNote shown={rows.length} total={filtered.length} />
        </>
      ))}
    </GCard>
  );
  return <MasterDetail list={list} detail={current ? <Detail key={current.key} m={current} onClose={close} /> : null} />;
}
